import { useState, useEffect, useRef } from 'react'
import Button from './Button'

/**
 * Reusable DataTable component with sorting, pagination, and page size controls
 * 
 * @param {Array} columns - Array of column definitions
 * @param {Array} data - Array of data objects
 * @param {Object} pagination - Pagination object with current_page, per_page, total, last_page, from, to
 * @param {Function} onPageChange - Callback when page changes (page) => void
 * @param {Function} onPageSizeChange - Callback when page size changes (perPage) => void
 * @param {Function} onSort - Callback when sorting changes (sortBy, sortOrder) => void
 * @param {string} sortBy - Current sort column
 * @param {string} sortOrder - Current sort order ('asc' or 'desc')
 * @param {boolean} loading - Loading state
 * @param {Array} selectedItems - Array of selected item IDs
 * @param {Function} onSelectAll - Callback for select all checkbox
 * @param {Function} onSelectItem - Callback for individual item selection (itemId) => void
 * @param {Function} renderRow - Custom render function for rows (item, index) => ReactNode
 * @param {number} perPage - Current page size
 * @param {Array} pageSizeOptions - Array of page size options [10, 25, 50, ...] or ['all']
 */
const DataTable = ({
  columns = [],
  data = [],
  pagination = {},
  onPageChange,
  onPageSizeChange,
  onSort,
  sortBy = 'created_at',
  sortOrder = 'desc',
  loading = false,
  selectedItems = [],
  onSelectAll,
  onSelectItem,
  renderRow,
  perPage = 15,
  pageSizeOptions = [10, 25, 50, 75, 100, 150, 200, 500, 'all'],
}) => {
  const selectAllCheckboxRef = useRef(null)
  const isAllSelected = selectedItems.length === data.length && data.length > 0
  const isIndeterminate = selectedItems.length > 0 && selectedItems.length < data.length

  // Set indeterminate state on select all checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current && onSelectAll) {
      selectAllCheckboxRef.current.indeterminate = isIndeterminate
    }
  }, [isIndeterminate, onSelectAll])

  const handleSort = (column) => {
    if (onSort && column.sortable !== false) {
      if (sortBy === column.key) {
        // Toggle sort order
        onSort(column.key, sortOrder === 'asc' ? 'desc' : 'asc')
      } else {
        // Set new sort column
        onSort(column.key, 'asc')
      }
    }
  }

  const handlePageSizeChange = (e) => {
    if (onPageSizeChange) {
      const newPerPage = e.target.value === 'all' ? 'all' : parseInt(e.target.value)
      onPageSizeChange(newPerPage)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
        </svg>
        <p className="text-lg font-medium mb-2">No data found</p>
      </div>
    )
  }

  return (
    <>
      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {onSelectAll && (
                <th className="px-6 py-3 text-left w-12">
                  <input
                    type="checkbox"
                    ref={selectAllCheckboxRef}
                    checked={isAllSelected}
                    onChange={onSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.sortable !== false ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                  }`}
                  onClick={() => column.sortable !== false && handleSort(column)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {column.sortable !== false && (
                      <>
                        {sortBy === column.key ? (
                          <svg
                            className={`w-4 h-4 ${sortOrder === 'asc' ? '' : 'transform rotate-180'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                          </svg>
                        )}
                      </>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {renderRow
              ? data.map((item, index) => renderRow(item, index, selectedItems, onSelectItem))
              : data.map((item, index) => (
                  <tr
                    key={item.id || index}
                    className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors ${
                      selectedItems.includes(item.id) ? 'bg-blue-100' : ''
                    }`}
                  >
                    {onSelectItem && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => onSelectItem(item.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`px-6 py-4 ${
                          column.nowrap !== false ? 'whitespace-nowrap' : ''
                        } text-sm ${
                          column.headerClassName || ''
                        }`}
                      >
                        {column.render ? column.render(item, index) : item[column.key] || <span className="text-gray-400 italic">-</span>}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {/* Pagination and Page Size */}
      <div className="mt-4 flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0 border-t border-gray-200 pt-4">
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{pagination.from || 0}</span> to <span className="font-medium">{pagination.to || 0}</span> of{' '}
            <span className="font-medium">{pagination.total || 0}</span> results
          </div>
          {onPageSizeChange && (
            <div className="flex items-center space-x-2">
              <label htmlFor="per-page" className="text-sm text-gray-700">
                Show:
              </label>
              <select
                id="per-page"
                value={perPage}
                onChange={handlePageSizeChange}
                className="px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option === 'all' ? 'All' : option}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {pagination.last_page > 1 && onPageChange && (
          <div className="flex items-center space-x-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(1)}
              disabled={pagination.current_page === 1}
              className="px-3 py-1.5"
            >
              First
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="px-3 py-1.5"
            >
              Previous
            </Button>
            <span className="px-3 py-1.5 text-sm text-gray-700">
              Page <span className="font-medium">{pagination.current_page}</span> of <span className="font-medium">{pagination.last_page}</span>
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className="px-3 py-1.5"
            >
              Next
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onPageChange(pagination.last_page)}
              disabled={pagination.current_page === pagination.last_page}
              className="px-3 py-1.5"
            >
              Last
            </Button>
          </div>
        )}
      </div>
    </>
  )
}

export default DataTable

